import { DisplayRightsCopyLinkButton } from '@/components/display-rights/DisplayRightsCopyLinkButton';
import { DisplayRightsExpiryLine } from '@/components/display-rights/DisplayRightsExpiryLine';
import { DisplayRightsExpiryNotice } from '@/components/display-rights/DisplayRightsExpiryNotice';
import { DisplayRightsStatusBadges } from '@/components/display-rights/DisplayRightsStatusBadges';
import { DisplayRightsTermsLine } from '@/components/display-rights/DisplayRightsTermsLine';
import {
  canRenewDisplayRights,
  type DisplayRightsMetaState,
} from '@/components/display-rights/display-rights-status';
import { Button, HStack, VStack } from '@chakra-ui/react';
import type {
  DisplayRightsProposalSummary,
  MentionBoostAuthorizationStatus,
} from '@fluenceur/types';
import { Refresh } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface MentionBoostDisplayRightsRowProps {
  displayRights: DisplayRightsProposalSummary | null;
  /** Absent until an authorization exists: there is no Instagram answer yet. */
  metaState?: DisplayRightsMetaState;
  /** The authorization the terms hang off; needed to reopen the composer. */
  authorizationId?: string | null;
  authorizationStatus?: MentionBoostAuthorizationStatus;
  /** Opens the composer, pre-filled with the terms already agreed. */
  onRenew?: (authorizationId: string) => void;
}

/**
 * The compensation row of the mention panel: the crossed state, the terms, the
 * deadline, and the link.
 *
 * Rendered ONCE, above whichever decision surface is showing — the eligibility
 * panel before launch, the delivery view after. It used to live inside the
 * eligibility panel, which meant every mention whose boost was already running
 * (the common case in real use) lost its badges and its link on this surface
 * while the contact card and the co-publicites table still showed them. The
 * ticket asks the three surfaces to read the same; a branch of the panel is not
 * a place for that promise to hide.
 */
export function MentionBoostDisplayRightsRow({
  displayRights,
  metaState,
  authorizationId,
  authorizationStatus,
  onRenew,
}: MentionBoostDisplayRightsRowProps) {
  const { t } = useTranslation();

  if (!metaState) {
    return null;
  }

  // The renewal is offered where the composer can already be reached: the panel
  // owns that flow (the post-request proposal step), so this reuses it rather
  // than inventing a second way in.
  const renewableAuthorizationId =
    onRenew && authorizationId && canRenewDisplayRights(displayRights, authorizationStatus)
      ? authorizationId
      : null;

  return (
    <VStack align="stretch" gap={0} mt={3} data-testid="mention-boost-display-rights-block">
      <HStack
        justify="space-between"
        gap={2}
        flexWrap="wrap"
        px={3}
        py={2.5}
        border="1px solid"
        borderColor="gray.100"
        rounded="lg"
        bg="white"
        data-testid="mention-boost-display-rights"
      >
        {/* The terms in force sit under the badges: the state says where the
            negotiation stands, the line says what was actually agreed — and, when
            it applies, that the agreement was affirmed off platform. The deadline
            closes the block, because it is the part that needs acting on. */}
        <VStack align="start" gap={1} minW="0">
          <DisplayRightsStatusBadges proposal={displayRights} metaState={metaState} size="xs" />
          <DisplayRightsTermsLine proposal={displayRights} showVersion />
          <DisplayRightsExpiryLine proposal={displayRights} />
        </VStack>
        <DisplayRightsCopyLinkButton proposal={displayRights} />
      </HStack>

      <DisplayRightsExpiryNotice proposal={displayRights} />

      {renewableAuthorizationId && (
        <Button
          mt={2}
          size="sm"
          variant="outline"
          width="full"
          onClick={() => onRenew?.(renewableAuthorizationId)}
          data-testid="mention-boost-display-rights-renew"
        >
          <Refresh width={14} height={14} />
          {t('displayRights.actions.renewRights')}
        </Button>
      )}
    </VStack>
  );
}
